/**
 * When the email unlock appears, and what happens when it does (SKE-4166,
 * phase 3).
 *
 * SOFT GATE, NOT A WALL
 * ---------------------
 * The demo is fully interactive from the first paint and stays that way whether
 * or not an email is ever given. The gate is an *offer* — "want this board
 * personalised to your client?" — so:
 *
 *   - it never renders before the visitor has actually done something. Asking
 *     on arrival converts worse and, more importantly, would gate the product
 *     experience the ad promised;
 *   - it opens once, automatically, after the visitor's FIRST approve /
 *     request-change (`GATE_ACTION_THRESHOLD` status actions) — the point at
 *     which they have felt one decision land on the team's side and the offer
 *     reads as a next step rather than a toll. A note left on a post is
 *     engagement but not a decision, so it does not trip the gate;
 *   - dismissing it is free and is remembered for the visit, so the demo can
 *     never nag. The visitor can reopen it themselves at any time from the
 *     persistent unlock button.
 *
 * `demo-gate-shown` is fired at most once per visit, so it is a clean
 * denominator for the email-unlock KPI.
 */

import {
  useCallback,
  useEffect,
  useRef,
  useState,
  useSyncExternalStore,
} from 'react'

import { trackDemo } from './demoAnalytics'
import { isDemoLeadCaptureConfigured } from './demoLead'
import { getDemoStoreSnapshot, subscribeDemoStore } from './demoStore'
import {
  getDemoUnlockSnapshot,
  subscribeDemoUnlock,
  type DemoUnlockState,
} from './demoUnlock'

/** Status decisions (approve / request change) before the offer is made. */
export const GATE_ACTION_THRESHOLD = 1

const DISMISSED_KEY = 'sked.demo.approvals.gate-dismissed.v1'

const readDismissed = (): boolean => {
  try {
    return globalThis.window?.sessionStorage.getItem(DISMISSED_KEY) === '1'
  } catch {
    return false
  }
}

const writeDismissed = (): void => {
  try {
    globalThis.window?.sessionStorage.setItem(DISMISSED_KEY, '1')
  } catch {
    // Storage disabled — the in-memory state still suppresses it this session.
  }
}

export interface DemoGateState {
  /** Render the gate at all? False when the lead pipeline isn't configured. */
  isAvailable: boolean
  isOpen: boolean
  isUnlocked: boolean
  unlock: DemoUnlockState | null
  /** Actions taken so far — sent with the lead as an engagement signal. */
  actionCount: number
  open: () => void
  dismiss: () => void
}

export const useDemoGate = (): DemoGateState => {
  const store = useSyncExternalStore(subscribeDemoStore, getDemoStoreSnapshot)
  const unlock = useSyncExternalStore(
    subscribeDemoUnlock,
    getDemoUnlockSnapshot
  )

  const actionCount = store?.actions.length ?? 0
  // The gate is armed by a real DECISION, not any interaction: a note
  // (`kind: 'comment'`) is engagement but shouldn't trip "make this yours".
  const statusActionCount =
    store?.actions.filter((entry) => (entry.kind ?? 'status') === 'status')
      .length ?? 0
  const isUnlocked = unlock !== null
  const isAvailable = isDemoLeadCaptureConfigured()

  const [isOpen, setIsOpen] = useState(false)
  const [isDismissed, setIsDismissed] = useState(readDismissed)

  // Auto-open fires once per visit; after that the visitor is in control.
  const hasAutoOpenedRef = useRef(false)
  useEffect(() => {
    if (
      hasAutoOpenedRef.current ||
      !isAvailable ||
      isUnlocked ||
      isDismissed ||
      statusActionCount < GATE_ACTION_THRESHOLD
    ) {
      return
    }
    hasAutoOpenedRef.current = true
    setIsOpen(true)
  }, [isAvailable, isUnlocked, isDismissed, statusActionCount])

  // One `demo-gate-shown` per visit, whatever opened it — the KPI denominator.
  const hasTrackedShownRef = useRef(false)
  useEffect(() => {
    if (!isOpen || hasTrackedShownRef.current) {
      return
    }
    hasTrackedShownRef.current = true
    trackDemo('demo-gate-shown', { action_count: actionCount })
  }, [isOpen, actionCount])

  const open = useCallback(() => setIsOpen(true), [])

  const dismiss = useCallback(() => {
    setIsOpen(false)
    // Only a genuinely locked visitor can "dismiss"; closing the confirmation
    // after a successful unlock is not a dismissal and must not be counted.
    if (getDemoUnlockSnapshot() !== null || readDismissed()) {
      return
    }
    setIsDismissed(true)
    writeDismissed()
    trackDemo('demo-gate-dismissed', { action_count: actionCount })
  }, [actionCount])

  return {
    isAvailable,
    isOpen,
    isUnlocked,
    unlock,
    actionCount,
    open,
    dismiss,
  }
}
